import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { FaMoon, FaSun, FaTimes, FaArrowLeft, FaHome, FaShareAlt } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import ActionButton from '../widgets/ActionButton';
import Buymeacoffee from '../widgets/BuyMeACoffee';

const Navbar = () => {
    // Assuming ThemeContext provides these
    const { darkMode, toggleDarkMode } = useContext(ThemeContext);
    const [scrolled, setScrolled] = useState(false);
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);
    const [buttonType, setButtonType] = useState(null); // 'close', 'back', 'home', or null
    const [showShareButton, setShowShareButton] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Determine if we are on the Home Page
    const isHomePage = location.pathname === '/';
    // Determine if we are on a detail page (where adaptive buttons appear)
    const isDetailPage = !isHomePage;
    const pathSegments = location.pathname.split('/').filter(s => s.length > 0);

    const isArticleDetail =
        pathSegments[0] === 'articles' && pathSegments.length > 1;

    const isDemoDetail =
        pathSegments[0] === 'demos' && pathSegments.length > 1;

    const excludedPaths = ['/articles/featured'];

    const isDetailsPage =
        (isArticleDetail || isDemoDetail) &&
        !excludedPaths.includes(location.pathname);

    // Use useMemo to safely cache theme-related styles based on environment/darkMode
    const themeClasses = useMemo(() => {
        // Define primary and secondary colors for a modern, theme-aware palette
        const primaryColor = 'indigo'; // Primary for action buttons/highlights
        const secondaryColor = 'pink'; // Secondary for fixed toggle/accent

        return {
            // Navbar colors (Slightly transparent when scrolled for the modern look)
            navbar: darkMode
                ? 'bg-gray-900 border-gray-700'
                : 'bg-white border-gray-200',

            // Text color for nav links
            text: darkMode
                ? 'text-gray-100 hover:text-indigo-400'
                : 'text-gray-900 hover:text-indigo-600',

            // Toggler icon color
            togglerIcon: darkMode ? 'text-gray-100' : 'text-gray-900',

            // 1. Primary Button (Share Button) - Uses the primary accent color
            btnPrimary: `bg-${primaryColor}-600 text-white 
                hover:bg-${primaryColor}-700 focus:ring-4 focus:ring-${primaryColor}-500/50 
                shadow-md`,
            
            // 2. Secondary/Outline Button (Adaptive Button) - Uses theme-aware outline
            btnOutline: darkMode
                ? `border-2 border-gray-500 text-gray-100 hover:bg-gray-700 focus:ring-4 focus:ring-gray-500/50`
                : `border-2 border-gray-400 text-gray-800 hover:bg-gray-100 focus:ring-4 focus:ring-gray-300/50`,
            
            // Fixed Toggle Button Colors (using the secondary accent color)
            fixedBtnBg: `bg-${secondaryColor}-500 hover:bg-${secondaryColor}-600`,
            fixedBtnShadow: darkMode ? 'shadow-2xl shadow-gray-700/50' : 'shadow-2xl shadow-gray-400/50',
        };
    }, [darkMode]);

    // Nav Items (Only used on Home Page)
    const navItems = ['Home', 'Articles', 'Videos', 'Demos', 'About', 'Gallery', 'Projects', 'Journey', 'Contact'];

    // Detect scroll to add navbar shadow
    const handleScroll = useCallback(() => {
        setScrolled(window.scrollY > 50);
    }, []);

    // --- Share Logic ---
    const handleShare = useCallback(async () => {
        const title = document.title || 'Check this out!';
        const url = window.location.href;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    url: url,
                });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            // Fallback: Copy link to clipboard
            await navigator.clipboard.writeText(url);
            alert(`Link copied to clipboard! Share the current page: ${url}`);
        }
    }, []);

    // --- Adaptive Button Logic ---
    useEffect(() => {
        const { pathname, state } = location;
        const fromHome = state?.fromHome;
        const referrer = document.referrer;
        const historyDepth = window.history.length;

        
        // Always show the share button on detail pages
        setShowShareButton(isDetailsPage);

        // Determine the Adaptive Button type
        if (pathname === '/') {
            setButtonType(null);
        } else if (fromHome) {
            setButtonType('close');
        } else if (referrer && !referrer.includes(window.location.origin)) {
            setButtonType('home');
        } else if (historyDepth > 2) {
            setButtonType('back');
        } else {
            setButtonType('home');
        }
    }, [location, isDetailsPage]);

    const toggleNavbar = () => setIsNavCollapsed(!isNavCollapsed);

    // Action handler for the standard adaptive button (Back/Close/Home)
    const handleAdaptiveButtonAction = () => {
        if (buttonType === 'close' || buttonType === 'back') {
            navigate(-1);
        } else {
            navigate('/', { state: { fromDetail: true } });
        }
        setIsNavCollapsed(true);
    };

    const handleNavClick = (e, sectionId) => {
        e.preventDefault();
        setIsNavCollapsed(true);

        // Map 'home' click to the root path
        const targetPath = sectionId === 'home' ? '/' : `/${sectionId}`; 

        if (location.pathname === '/') {
            const section = document.getElementById(sectionId);
            if (section) {
                // Scroll behavior for same-page links
                window.scrollTo({
                    top: section.offsetTop - 64, // Adjusted for fixed navbar height (16 = 4rem = 64px)
                    behavior: 'smooth',
                });
            }
        } else {
            // Navigate to the correct page/path
            navigate(targetPath, { state: { scrollTo: sectionId, fromNav: true } });
        }
    };

    // Attach scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // --- Render Share Button (Primary Button Style) ---
    const renderShareButton = () => {
        // 1. Determine the correct label based on the path
        const pathSegments = location.pathname.split('/').filter(s => s.length > 0);
        const isArticle = pathSegments[0] === 'articles';
        const desktopLabel = isArticle ? 'Share Article' : 'Share Demo';

        // 2. Prepare the combined text content for the button
        const buttonText = (
            <>
                <span className="hidden sm:inline">{desktopLabel}</span>
                <span className="sm:hidden">Share</span>
            </>
        );

        // 3. Return the ActionButton component
        return (
            <ActionButton
                text={buttonText}
                onClick={handleShare}
                Icon={FaShareAlt} // Pass the imported icon component
                iconPlacement="left"
                // Adjusted classes for a smaller button that fits the nav
                className="!px-3 !py-1 !h-auto mr-1 rounded-full text-sm font-semibold" 
            />
        );
    };

    // --- Render Adaptive Button (Secondary Outline Style) ---
    const renderAdaptiveButton = () => {
        if (!buttonType) return null;

        let label, IconComponent;

        switch (buttonType) {
            case 'close':
                label = 'Close';
                IconComponent = FaTimes;
                break;
            case 'back':
                label = 'Back';
                IconComponent = FaArrowLeft;
                break;
            case 'home':
            default:
                label = 'Home';
                IconComponent = FaHome;
                break;
        }

        return (
            <button
                // Secondary/Outline button style
                className={`flex items-center text-sm font-semibold rounded-full px-3 py-1 transition-all duration-300 transform hover:scale-[1.05] ${themeClasses.btnOutline} shadow-md`}
                onClick={handleAdaptiveButtonAction}
                aria-label={label}
            >
                <IconComponent size={14} className="mr-1.5" />
                <span className="hidden sm:inline">{label}</span>
                {/* On mobile, only the icon will show due to the hidden sm:inline on label */}
            </button>
        );
    };

    // --- Combined Adaptive/Share Button Group ---
    const renderButtonControls = () => {
        // Only render the controls if we're on a page that isn't the Home landing page
        if (!isDetailPage) return null;

        return (
            <div className="flex items-center space-x-2">
                {/* Renders Adaptive Button (Secondary Style) if buttonType is determined */}
                {renderAdaptiveButton()} 
                {/* Renders Share Button (Primary Style) if showShareButton is true */}
                {showShareButton && renderShareButton()}
                {isDetailsPage && <Buymeacoffee />}
            </div>
        );
    };


    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 border-b ${themeClasses.navbar}
                ${scrolled ? 'shadow-lg backdrop-blur-sm' : ''} 
                lg:flex lg:justify-center`}
            >
                <div className="container mx-auto px-4 max-w-7xl flex justify-between items-center h-16">
                    
                    {/* Left Side: Brand/Logo */}
                    <button 
                        className="flex items-center font-bold text-xl cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md p-1 -m-1"
                        onClick={() => navigate('/')}
                        aria-label="Navigate to Home Page"
                    >
                        <img
                            src="/logo192.png"
                            alt="Varun Rao Logo"
                            width="30"
                            height="30"
                            // LOGIC CHANGE: Only add margin if text follows (i.e., only on Home Page)
                            className={isHomePage ? "mr-2" : ""}
                        />
                        {/* LOGIC CHANGE: Render text ONLY on Home Page */}
                        {isHomePage && (
                             <span className={darkMode ? 'text-white' : 'text-gray-900'}>Varun</span>
                        )}
                    </button>
                    
                    {/* Right Side: Controls and Navigation */}
                    <div className="flex items-center space-x-3">
                        
                        {/* Dynamic Content based on Page Type */}
                        {isHomePage ? (
                            <>
                                {/* Desktop Nav Links (Home Page Only) */}
                                <div className="hidden lg:flex lg:items-center" id="navbarNav">
                                    <ul className="flex lg:ml-auto lg:space-x-1">
                                        {navItems.map((item) => (
                                            <li key={item}>
                                                <button
                                                    className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${themeClasses.text}`}
                                                    onClick={(e) => handleNavClick(e, item.toLowerCase())}
                                                    aria-label={`Scroll to ${item} section`}
                                                >
                                                    {item}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                
                                {/* Mobile Toggler (Home Page Only) */}
                                <button
                                    className={`lg:hidden p-2 rounded-md transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${themeClasses.togglerIcon}`}
                                    type="button"
                                    aria-controls="navbarNav"
                                    aria-expanded={!isNavCollapsed}
                                    aria-label="Toggle main navigation menu"
                                    onClick={toggleNavbar}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isNavCollapsed ? "M4 6h16M4 12h16M4 18h16" : "M6 18L18 6M6 6l12 12"}></path>
                                    </svg>
                                </button>
                            </>
                        ) : (
                            // Detail Page Button Group
                            renderButtonControls()
                        )}

                        {/* Theme Toggle Button (Always visible) */}
                        <button
                            onClick={toggleDarkMode}
                            className={`p-2 rounded-full transition-colors duration-300 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${themeClasses.togglerIcon}`}
                            aria-label={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
                        >
                            {darkMode 
                                ? <FaSun size={18} /> 
                                : <FaMoon size={18} />
                            }
                        </button>
                        
                    </div>
                </div>

                {/* Mobile Menu Collapse (Only applicable on Home Page) */}
                {isHomePage && (
                    <div className={`lg:hidden ${isNavCollapsed ? 'hidden' : 'block'} ${themeClasses.navbar} border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                            {navItems.map((item) => (
                                <button
                                    key={`mobile-${item}`}
                                    className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors ${themeClasses.text}`}
                                    onClick={(e) => handleNavClick(e, item.toLowerCase())}
                                    aria-label={`Scroll to ${item} section`}
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
            </nav>
            
            {/* Added a small placeholder to prevent content from hiding under the fixed nav */}
            <div className="h-16"></div>
        </>
    );
};

export default Navbar;